import React, { useState, useEffect } from "react";
import {
  Target,
  Save,
  CalendarDays,
  Flag,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${
      type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
    } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">×</button>
    </div>
  );
};

const HealthGoals = () => {
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [goalsData, setGoalsData] = useState({
    healthGoals: [],
    healthGoalsOther: "",
    targetTimeFrame: "",
    previousAttempts: false,
    previousApproach: "",
    challenges: ""
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [showOtherGoal, setShowOtherGoal] = useState(false);

  const healthGoalsOptions = [
    "To Prevent Diseases in Future",
    "To Manage Existing Conditions",
    "Weight Loss",
    "Weight Gain",
    "Maintain steady weight",
    "Reduce Fat Percentage",
    "Gain Muscle Mass",
    "Improve Cardiovascular Fitness (Stamina)",
    "Sport Specific Conditioning",
    "Postural Correction",
    "Improve Flexibility",
    "Overall Wellness",
    "Other"
  ];

  const targetTimeFrameOptions = [
    "1 month",
    "2 months",
    "3 months",
    "4 months",
    "5 months",
    "6 months",
    "8 months",
    "10 months",
    "12 months",
    "16 weeks",
    "20 weeks",
    "24 weeks",
    "Other"
  ];

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    fetchGoalsData();
  }, []);

  const fetchGoalsData = async () => {
    try {
      setLoading(true);
      const res = await getProfileStepDetails();
      
      if (res?.status) {
        const goalsFields = res?.profile_completion?.steps?.goals?.fields;
        
        const fieldMap = {};
        goalsFields.forEach((item) => {
          fieldMap[item.name] = item.value;
        });
        
        console.log("Fetched Goals Data:", fieldMap);
        
        let healthGoalsArray = [];
        if (fieldMap.healthGoals) {
          if (typeof fieldMap.healthGoals === 'string') {
            healthGoalsArray = fieldMap.healthGoals.split(',').map(s => s.trim());
          } else if (Array.isArray(fieldMap.healthGoals)) {
            healthGoalsArray = fieldMap.healthGoals;
          }
        }
        
        setGoalsData({
          healthGoals: healthGoalsArray,
          healthGoalsOther: "",
          targetTimeFrame: fieldMap.targetTimeFrame || "",
          previousAttempts: fieldMap.previousAttempts || false,
          previousApproach: fieldMap.previousApproach || "",
          challenges: fieldMap.challenges || ""
        });
        
        setShowOtherGoal(healthGoalsArray.includes("Other"));
      }
    } catch (error) {
      console.error("Error fetching goals data:", error);
      addToast("Failed to load goals data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleHealthGoalToggle = (goal) => {
    setGoalsData(prev => {
      const current = [...prev.healthGoals];
      if (current.includes(goal)) {
        return { ...prev, healthGoals: current.filter(item => item !== goal) };
      } else {
        return { ...prev, healthGoals: [...current, goal] };
      }
    });
    if (formErrors.healthGoals) {
      setFormErrors(prev => ({ ...prev, healthGoals: "" }));
    }
    
    if (goal === "Other") {
      setShowOtherGoal(!goalsData.healthGoals.includes("Other"));
    }
  };

  const handleInputChange = (field, value) => {
    setGoalsData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (goalsData.healthGoals.length === 0) {
      errors.healthGoals = "Please select at least one health goal";
    }
    
    if (!goalsData.targetTimeFrame) {
      errors.targetTimeFrame = "Please select target time frame";
    }
    
    if (goalsData.previousAttempts && !goalsData.previousApproach) {
      errors.previousApproach = "Please describe the approach used";
    }
    
    if (!goalsData.challenges) {
      errors.challenges = "Please describe any barriers or challenges";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSaveChanges = async () => {
  if (validateForm()) {
    setIsSaving(true);
    
    try {
      // Prepare final health goals array
      let finalHealthGoals = [...goalsData.healthGoals];
      if (showOtherGoal && goalsData.healthGoalsOther.trim()) {
        // Replace "Other" with the actual other goal text
        const otherIndex = finalHealthGoals.indexOf("Other");
        if (otherIndex !== -1) {
          finalHealthGoals[otherIndex] = goalsData.healthGoalsOther.trim();
        } else {
          finalHealthGoals.push(goalsData.healthGoalsOther.trim());
        }
      }
      
      const goalsPayload = {
        healthGoals: finalHealthGoals.join(", "),
        targetTimeFrame: goalsData.targetTimeFrame,
        previousAttempts: goalsData.previousAttempts,
        previousApproach: goalsData.previousApproach || null,
        challenges: goalsData.challenges
      };
      
      const response = await updateProfileStep("goals", goalsPayload);
      
      if (response?.status) {
        addToast("Health goals saved successfully!", "success");
        
        // 🔥 ADD THIS LINE - Refresh data after save
        await fetchGoalsData();
        
        // Optionally show completion message or redirect
        setTimeout(() => {
          addToast("Profile completed successfully! 🎉", "success");
        }, 1500);
      } else {
        addToast(response?.message || "Failed to save data", "error");
      }
    } catch (error) {
      console.error("Save error:", error);
      addToast(error.message || "Failed to save health goals", "error");
    } finally {
      setIsSaving(false);
    }
  } else {
    addToast("Please fill in all required fields", "error");
    const firstErrorField = Object.keys(formErrors)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  }
};
  const handleDiscard = () => {
    fetchGoalsData();
    setFormErrors({});
    addToast("All changes discarded", "info");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Container */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Health Goals
        </h2>
        <p className="text-[#98a19b] text-sm">
          Define your health and fitness goals to receive personalized plans.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Health Goals */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Target size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Health & Fitness Goals <span className="text-red-500">*</span>
              </h3>
            </div>

            <p className="text-[12px] text-[#98a19b] mb-4">
              Select all that apply to your health and fitness journey.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto p-2 border border-[#e1e6e1] rounded-md">
              {healthGoalsOptions.map((goal) => (
                <label key={goal} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={goalsData.healthGoals.includes(goal)}
                    onChange={() => handleHealthGoalToggle(goal)}
                    className="w-4 h-4 accent-green-500"
                  />
                  <span className="text-[13px] text-[#24312d]">{goal}</span>
                </label>
              ))}
            </div>
            {formErrors.healthGoals && (
              <p className="text-red-500 text-xs mt-2">{formErrors.healthGoals}</p>
            )}

            {showOtherGoal && (
              <div className="mt-3">
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Mention Other Goal
                </label>
                <input
                  type="text"
                  value={goalsData.healthGoalsOther}
                  onChange={(e) => handleInputChange("healthGoalsOther", e.target.value)}
                  placeholder="Please specify your goal"
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
            )}
          </div>

          {/* Target Time Frame */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Target Time Frame <span className="text-red-500">*</span>
              </h3>
            </div>

            <p className="text-[12px] text-[#98a19b] mb-4">
              How long do you want to achieve your goals?
            </p>

            <select
              name="targetTimeFrame"
              value={goalsData.targetTimeFrame}
              onChange={(e) => handleInputChange("targetTimeFrame", e.target.value)}
              className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${
                formErrors.targetTimeFrame
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
              }`}
            >
              <option value="">Select time frame</option>
              {targetTimeFrameOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {formErrors.targetTimeFrame && (
              <p className="text-red-500 text-xs mt-2">{formErrors.targetTimeFrame}</p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Previous Attempts */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Previous Attempts
              </h3>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Any previous attempts to achieve these goals?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={goalsData.previousAttempts}
                    onChange={(e) => handleInputChange("previousAttempts", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {goalsData.previousAttempts ? "Yes" : "No"}
                  </span>
                </label>
              </div>
            </div>

            {goalsData.previousAttempts && (
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  What approach was used? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="previousApproach"
                  value={goalsData.previousApproach}
                  onChange={(e) => handleInputChange("previousApproach", e.target.value)}
                  rows="3"
                  placeholder="e.g., Followed keto diet for 3 months, lost 5 kg but regained later."
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 ${
                    formErrors.previousApproach
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
                />
                {formErrors.previousApproach && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.previousApproach}</p>
                )}
              </div>
            )}
          </div>

          {/* Barriers & Challenges */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={18} className="text-yellow-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Barriers & Challenges <span className="text-red-500">*</span>
              </h3>
            </div>

            <p className="text-[12px] text-[#98a19b] mb-4">
              Any barriers or challenges you've faced in achieving your health goals?
            </p>

            <textarea
              name="challenges"
              value={goalsData.challenges}
              onChange={(e) => handleInputChange("challenges", e.target.value)}
              rows="4"
              placeholder="e.g., Lack of consistency, late working hours, and sweet cravings."
              className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 ${
                formErrors.challenges
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
              }`}
            />
            {formErrors.challenges && (
              <p className="text-red-500 text-xs mt-1">{formErrors.challenges}</p>
            )}
          </div>

          {/* Save Button */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <h3 className="text-[15px] font-semibold text-[#24312d] mb-2">
              Save Goals
            </h3>

            <p className="text-[12px] text-[#98a19b] leading-5 mb-5">
              Saving your goals will help us create a personalized plan tailored to your needs.
            </p>

            <button 
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="w-full h-[42px] bg-[#57b26a] text-white rounded-lg text-sm flex items-center justify-center gap-2 mb-3 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={15} />
                  Save Changes
                </>
              )}
            </button>

            <button 
              onClick={handleDiscard}
              disabled={isSaving}
              className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-sm text-[#57b26a] hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthGoals;